import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
    getCurrentTimezoneInfo,
    formatTimezoneDisplay,
} from '../utils/timezone-helpers';
import type { SupportedTimezone } from '../constants';
import { SUPPORTED_TIMEZONES } from '../constants';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from '../../../components/select';

interface TimePickerProps {
    timeZone: string;
    startTimestamp: number;
    onTimeSelect?: (timestamp: number) => void;
    initialSelectedTime?: number;
    onTimezoneChange?: (timezone: string) => void;
    allowTimeZoneChange?: boolean;
    /** When provided, used for dropdown options (e.g. includes unsupported browser timezone) */
    availableTimezones?: string[];
    freeHours?: string[];
    startHour?: number;
    endHour?: number;
    startMinute?: number;
    endMinute?: number;
    stepMinutes?: number;
    variant?: 'grid' | 'list' | 'listCompact';
    useManualDropdown?: boolean;
}

interface TimeSlot {
    key: string;
    timestamp: number;
    displayTime: string;
    period: 'AM' | 'PM';
    isDisabled: boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
    timeZone,
    startTimestamp,
    onTimeSelect,
    initialSelectedTime,
    onTimezoneChange,
    allowTimeZoneChange = true,
    availableTimezones,
    freeHours,
    startHour = 9,
    endHour = 16,
    startMinute = 0,
    endMinute = 0,
    stepMinutes = 60,
    variant = 'grid',
    useManualDropdown = false,
}) => {
    const [selectedTime, setSelectedTime] = useState<number | null>(
        initialSelectedTime || null,
    );
    const [isTimezoneDropdownOpen, setIsTimezoneDropdownOpen] = useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
    const listContainerRef = useRef<HTMLDivElement | null>(null);
    const timezoneDropdownRef = useRef<HTMLDivElement | null>(null);
    const timezoneTriggerRef = useRef<HTMLButtonElement | null>(null);
    const timezoneOptionsRef = useRef<(HTMLButtonElement | null)[]>([]);

    // Next available working day logic is shared in utils as getNextAvailableWorkingDayTimestamp

    // Get timezone entries for dropdown (supported + any extra from availableTimezones)
    const timezoneEntries = useMemo(() => {
        if (availableTimezones?.length) {
            return availableTimezones.map((tzId) => [
                tzId,
                { timeZoneId: tzId },
            ]) as [string, { timeZoneId: string }][];
        }
        return Object.entries(SUPPORTED_TIMEZONES);
    }, [availableTimezones]);

    // Get timezone info for the selected date
    const selectedDateTzInfo = getCurrentTimezoneInfo(
        timeZone,
        new Date(startTimestamp),
    );

    // Select value: when using availableTimezones, use timeZoneId; otherwise use SUPPORTED key
    const currentTimezoneKey = useMemo(() => {
        if (
            availableTimezones?.length &&
            availableTimezones.includes(timeZone)
        ) {
            return timeZone;
        }
        const entry = Object.entries(SUPPORTED_TIMEZONES).find(
            ([, tz]) => tz.timeZoneId === timeZone,
        );
        return entry?.[0];
    }, [timeZone, availableTimezones]);

    // Get the date for display in the target timezone
    const getDisplayDate = (): string => {
        // startTimestamp is UTC midnight, so we format it directly in the target timezone
        const utcDate = new Date(startTimestamp);
        const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: timeZone,
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
        return formatter.format(utcDate);
    };

    const timeSlots = useMemo(() => {
        const slots: TimeSlot[] = [];

        if (stepMinutes <= 0) {
            return slots;
        }

        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        if (endTotalMinutes < startTotalMinutes) {
            return slots;
        }

        const now = new Date();
        // using the generic 'en' (English) locale is a direct cause of s exact ambiguity
        const nowInTargetTz = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const nowFormatted = nowInTargetTz.format(now);

        // Format dates for comparison - en-NZ produces DD/MM/YYYY
        const dateOnlyFormatter = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const selectedDateFormatted = dateOnlyFormatter.format(
            new Date(startTimestamp),
        );

        const [todayDateStr, todayTimeStr] = nowFormatted.split(', ');
        if (!todayDateStr || !todayTimeStr) {
            throw new Error(`Invalid formatted date: ${nowFormatted}`);
        }

        const isToday = todayDateStr === selectedDateFormatted;

        // Create normalized date objects for proper comparison (set to start of day)
        const todayStartOfDay = new Date(now);
        todayStartOfDay.setHours(0, 0, 0, 0);
        const selectedStartOfDay = new Date(startTimestamp);
        selectedStartOfDay.setHours(0, 0, 0, 0);

        let currentMinutesOfDay = 0;
        if (isToday) {
            const [hourStr, minuteStr] = todayTimeStr.split(':');
            if (!hourStr || !minuteStr) {
                throw new Error(`Invalid time part from: ${todayTimeStr}`);
            }
            const parsedHour = parseInt(hourStr, 10);
            const parsedMinute = parseInt(minuteStr, 10);
            if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) {
                throw new Error(`Invalid numeric time from: ${todayTimeStr}`);
            }
            currentMinutesOfDay = parsedHour * 60 + parsedMinute;
        }

        const dateFormatter = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const timeFormatter = new Intl.DateTimeFormat('en', {
            timeZone: timeZone,
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
        });

        const selectedDate = new Date(startTimestamp);
        const dateInTargetTz = dateFormatter.format(selectedDate);
        const [day, month, year] = dateInTargetTz.split('/').map(Number);

        if (year === undefined || month === undefined || day === undefined) {
            throw new Error(`Invalid date components from: ${dateInTargetTz}`);
        }

        for (
            let minuteOfDay = startTotalMinutes;
            minuteOfDay < endTotalMinutes;
            minuteOfDay += stepMinutes
        ) {
            const hour = Math.floor(minuteOfDay / 60);
            const minute = minuteOfDay % 60;

            const testDate = new Date(year, month - 1, day, hour, minute, 0, 0);

            const displayedTime = timeFormatter.format(testDate);
            const [displayedHourStr, displayedMinuteStr] =
                displayedTime.split(':');
            if (!displayedHourStr || !displayedMinuteStr) {
                throw new Error(
                    `Invalid hour from displayed time: ${displayedTime}`,
                );
            }
            const displayedHour = parseInt(displayedHourStr, 10);
            const displayedMinute = parseInt(displayedMinuteStr, 10);

            let timeTimestamp = testDate.getTime();

            if (displayedHour !== hour || displayedMinute !== minute) {
                const diffMinutes =
                    (hour - displayedHour) * 60 + (minute - displayedMinute);
                timeTimestamp = testDate.getTime() + diffMinutes * 60 * 1000;
            }

            const normalizedTime = timeFormatter.format(
                new Date(timeTimestamp),
            );

            const [normalizedHourStr, normalizedMinuteStr] =
                normalizedTime.split(':');
            if (!normalizedHourStr || !normalizedMinuteStr) {
                throw new Error(`Invalid normalized time: ${normalizedTime}`);
            }
            const normalizedHour = parseInt(normalizedHourStr, 10);
            const normalizedMinute = parseInt(normalizedMinuteStr, 10);

            const slotMinutesOfDay = normalizedHour * 60 + normalizedMinute;

            let isDisabled = false;
            if (isToday) {
                isDisabled = slotMinutesOfDay <= currentMinutesOfDay;
            } else {
                isDisabled =
                    selectedStartOfDay.getTime() < todayStartOfDay.getTime();
            }

            const period = normalizedHour >= 12 ? 'PM' : 'AM';
            let hour12 = normalizedHour % 12;
            if (hour12 === 0) {
                hour12 = 12;
            }
            const displayTime = `${String(hour12).padStart(2, '0')}:${normalizedMinuteStr} ${period}`;

            // Backend returns labels in format "9:00 AM" (no leading zero, space before AM/PM)
            const backendFormatLabel = `${hour12}:${normalizedMinuteStr} ${period}`;

            const isInFreeHours =
                typeof freeHours === 'undefined'
                    ? true
                    : freeHours.includes(backendFormatLabel);

            if (!isInFreeHours) {
                isDisabled = true;
            }

            const normalizedLabel = `${normalizedHourStr}:${normalizedMinuteStr}`;

            slots.push({
                key: normalizedLabel,
                timestamp: timeTimestamp,
                displayTime,
                period,
                isDisabled,
            });
        }

        return slots;
    }, [
        timeZone,
        startTimestamp,
        freeHours,
        startHour,
        endHour,
        startMinute,
        endMinute,
        stepMinutes,
    ]);

    // Handle clicks outside timezone dropdown for manual dropdown
    useEffect(() => {
        if (!useManualDropdown) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                timezoneDropdownRef.current &&
                !timezoneDropdownRef.current.contains(event.target as Node)
            ) {
                setIsTimezoneDropdownOpen(false);
            }
        };

        if (isTimezoneDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [useManualDropdown, isTimezoneDropdownOpen]);

    // Focus management: Move focus to selected/first item when dropdown opens, return to trigger when closes
    useEffect(() => {
        if (!useManualDropdown) {
            return;
        }

        if (isTimezoneDropdownOpen) {
            // When dropdown opens, find the selected timezone index or default to 0
            const selectedIndex = timezoneEntries.findIndex(
                ([, tz]) => tz.timeZoneId === timeZone,
            );
            const initialFocusIndex = selectedIndex >= 0 ? selectedIndex : 0;
            setFocusedOptionIndex(initialFocusIndex);

            // Focus the selected or first option after render
            requestAnimationFrame(() => {
                timezoneOptionsRef.current[initialFocusIndex]?.focus();
            });
        } else {
            // When dropdown closes, return focus to trigger and reset focused index
            setFocusedOptionIndex(-1);
            if (timezoneTriggerRef.current) {
                timezoneTriggerRef.current.focus();
            }
        }
    }, [useManualDropdown, isTimezoneDropdownOpen, timeZone, timezoneEntries]);

    // Sync local selected time state with prop changes
    useEffect(() => {
        if (
            typeof initialSelectedTime === 'number' &&
            initialSelectedTime !== selectedTime
        ) {
            setSelectedTime(initialSelectedTime);
            return;
        }

        if (
            typeof initialSelectedTime === 'undefined' &&
            selectedTime !== null
        ) {
            setSelectedTime(null);
        }
    }, [initialSelectedTime, selectedTime]);

    // Scroll to selected or first available time slot in list variants
    useEffect(() => {
        if (
            (variant !== 'list' && variant !== 'listCompact') ||
            !timeSlots.length
        ) {
            return;
        }

        const container = listContainerRef.current;
        if (!container) {
            return;
        }

        const scheduledScroll = () => {
            const targetTimestamp =
                typeof initialSelectedTime === 'number'
                    ? initialSelectedTime
                    : timeSlots.find((slot) => !slot.isDisabled)?.timestamp;

            if (!targetTimestamp) {
                return;
            }

            const targetButton = container.querySelector<HTMLButtonElement>(
                `button[data-timestamp="${targetTimestamp}"]`,
            );

            const buttonToScroll =
                targetButton ??
                container.querySelector<HTMLButtonElement>(
                    'button:not(:disabled)',
                );

            buttonToScroll?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        };

        const animationId = requestAnimationFrame(scheduledScroll);

        return () => {
            cancelAnimationFrame(animationId);
        };
    }, [initialSelectedTime, timeSlots, variant]);

    const timezoneCurrentTimeLabel = useMemo(() => {
        const formatter = new Intl.DateTimeFormat('en', {
            timeZone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        return formatter.format(new Date());
    }, [timeZone]);

    // Handle time selection
    const handleTimeSelect = (timestamp: number, isDisabled: boolean) => {
        if (isDisabled) return;

        setSelectedTime(timestamp);

        if (onTimeSelect) {
            onTimeSelect(timestamp);
        }
    };

    // Handle timezone change (value is timeZoneId when using availableTimezones, else SUPPORTED key)
    const handleTimezoneChange = (value: string) => {
        const newTimeZoneId = availableTimezones?.includes(value)
            ? value
            : SUPPORTED_TIMEZONES[value as SupportedTimezone]?.timeZoneId;
        if (newTimeZoneId && onTimezoneChange) {
            onTimezoneChange(newTimeZoneId);
        }
    };

    // Handle manual dropdown timezone change
    const handleManualTimezoneChange = (newTimeZoneId: string) => {
        if (onTimezoneChange) {
            onTimezoneChange(newTimeZoneId);
        }
        setIsTimezoneDropdownOpen(false);
    };

    const renderTimeSlots = () => {
        if (!timeSlots.length) {
            return (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                    No available times for the selected day.
                </div>
            );
        }

        const renderList = (containerClassName: string) => (
            <div
                className={cn(
                    'scrollbar-hide flex flex-col gap-1 overflow-y-auto pb-4',
                    containerClassName,
                )}
                ref={listContainerRef}
            >
                {timeSlots
                    .filter((slot) => !slot.isDisabled)
                    .map((slot) => (
                        <button
                            key={slot.key}
                            data-timestamp={slot.timestamp}
                            onClick={() =>
                                handleTimeSelect(
                                    slot.timestamp,
                                    slot.isDisabled,
                                )
                            }
                            disabled={slot.isDisabled}
                            className={cn(
                                'justify flex w-full items-center gap-1 rounded-2 border px-5 py-3 text-left text-base transition-colors',
                                slot.isDisabled
                                    ? 'cursor-not-allowed border-gray-900 text-gray-900 opacity-40'
                                    : selectedTime === slot.timestamp
                                      ? 'border-gray-900 bg-gray-900 text-white'
                                      : 'border-gray-900 text-gray-800 hover:border-gray-900 hover:bg-gray-900 hover:text-white',
                            )}
                        >
                            <span className="w-11">
                                {slot.displayTime.split(' ')[0]}
                            </span>
                            <span>{slot.period}</span>
                        </button>
                    ))}
            </div>
        );

        if (variant === 'list') {
            return renderList('h-108');
        }

        if (variant === 'listCompact') {
            return renderList('h-60');
        }

        return (
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {timeSlots
                    .filter((slot) => !slot.isDisabled)
                    .map((slot) => (
                        <button
                            key={slot.key}
                            onClick={() =>
                                handleTimeSelect(
                                    slot.timestamp,
                                    slot.isDisabled,
                                )
                            }
                            disabled={slot.isDisabled}
                            className={cn(
                                'rounded-1 border p-3 text-base font-medium transition-colors',
                                selectedTime === slot.timestamp
                                    ? 'bg-gray-900 text-white'
                                    : slot.isDisabled
                                      ? 'cursor-not-allowed border-gray-200 text-gray-400'
                                      : 'border-gray-900 text-gray-700 hover:bg-gray-900 hover:text-white',
                            )}
                        >
                            {slot.displayTime}
                        </button>
                    ))}
            </div>
        );
    };

    return (
        <div className="w-full">
            <div className="mb-5 space-y-2">
                <h2 className="text-4.5 font-medium text-gray-900">
                    What time works best?
                </h2>
                {variant !== 'list' && variant !== 'listCompact' && (
                    <p className="text-4.5 font-medium text-gray-500">
                        Showing times for <span>{getDisplayDate()}</span>
                    </p>
                )}

                {useManualDropdown ? (
                    <div className="relative" ref={timezoneDropdownRef}>
                        <button
                            ref={timezoneTriggerRef}
                            onClick={() => {
                                if (allowTimeZoneChange) {
                                    setIsTimezoneDropdownOpen(
                                        !isTimezoneDropdownOpen,
                                    );
                                }
                            }}
                            disabled={!allowTimeZoneChange}
                            className={cn(
                                'focus:outline-hidden flex items-center justify-start gap-2 border-none bg-transparent p-0 text-left text-sm font-medium text-gray-700 hover:bg-transparent',
                                !allowTimeZoneChange &&
                                    'cursor-default opacity-50',
                            )}
                            type="button"
                            aria-haspopup="listbox"
                            aria-expanded={isTimezoneDropdownOpen}
                        >
                            <Globe className="h-5 w-5 text-gray-500" />
                            <span>
                                {selectedDateTzInfo.shortName}
                                {variant === 'list' && (
                                    <span className="ml-2">
                                        (
                                        {timezoneCurrentTimeLabel.toLowerCase()}
                                        )
                                    </span>
                                )}
                            </span>
                            {allowTimeZoneChange && (
                                <ChevronDown className="ml-1 h-4.5 w-4.5 text-gray-700" />
                            )}
                        </button>

                        {isTimezoneDropdownOpen && allowTimeZoneChange && (
                            <div
                                className="absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg"
                                role="listbox"
                            >
                                {timezoneEntries.map(
                                    ([key, timezone], index) => {
                                        const isSelected =
                                            timeZone === timezone.timeZoneId;
                                        const isFocused =
                                            focusedOptionIndex === index;
                                        return (
                                            <button
                                                key={key}
                                                ref={(el) => {
                                                    timezoneOptionsRef.current[
                                                        index
                                                    ] = el;
                                                }}
                                                onClick={() =>
                                                    handleManualTimezoneChange(
                                                        timezone.timeZoneId,
                                                    )
                                                }
                                                onMouseEnter={() =>
                                                    setFocusedOptionIndex(index)
                                                }
                                                className={cn(
                                                    'block w-full px-4 py-3 text-left text-sm transition-colors hover:bg-gray-100',
                                                    isSelected &&
                                                        'bg-gray-50 font-medium',
                                                    isFocused && 'bg-gray-100',
                                                )}
                                                type="button"
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                {formatTimezoneDisplay(
                                                    timezone.timeZoneId,
                                                    new Date(startTimestamp),
                                                )}
                                            </button>
                                        );
                                    },
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <Select
                        value={currentTimezoneKey ?? undefined}
                        onValueChange={handleTimezoneChange}
                        disabled={!allowTimeZoneChange}
                    >
                        <SelectTrigger
                            className={cn(
                                'flex w-full items-center justify-start gap-2 border-none bg-transparent p-0 text-left text-sm font-medium text-gray-500 hover:bg-transparent',
                                !allowTimeZoneChange &&
                                    'cursor-default opacity-50',
                            )}
                        >
                            <Globe className="h-5 w-5 text-gray-500" />
                            <span>
                                {selectedDateTzInfo.shortName}
                                {variant === 'list' && (
                                    <span className="ml-1">
                                        (
                                        {timezoneCurrentTimeLabel.toLowerCase()}
                                        )
                                    </span>
                                )}
                            </span>
                        </SelectTrigger>
                        <SelectContent>
                            {timezoneEntries.map(([key, timezone]) => (
                                <SelectItem key={key} value={key}>
                                    {formatTimezoneDisplay(
                                        timezone.timeZoneId,
                                        new Date(startTimestamp),
                                    )}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {renderTimeSlots()}
        </div>
    );
};

export { TimePicker };
